import type { Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useMarketSelectorConfig, type MarketSelectorConfigContextProps } from './MarketSelectorConfigContext';
import type { MarketSelectorAPI } from './types';

export interface MarketSelectorContextProps extends MarketSelectorAPI, MarketSelectorConfigContextProps {
  blotterSearchInput: HTMLInputElement | null;
  setBlotterSearchInput: React.Dispatch<SetStateAction<HTMLInputElement | null>>;
  /** Whether or not to show things related to the advanced mode, such as the Advanced Mode toggle */
  showAdvancedMode: boolean;
  showCredentialsOnlyToggle: boolean;
  /** If set to true, the view will not render anything related to aggregations */
  disableAggregations: boolean;
  /** State that applies only in narrow mode */
  narrowModeConfig: {
    /** Show market selector blotter in narrow mode (hide aggregations) */
    showMarketSelectorBlotter: boolean;
    setShowMarketSelectorBlotter: (showMarketSelectorBlotter: boolean) => void;
  };
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const MarketSelectorContext = createContext<MarketSelectorContextProps | undefined>(undefined);

export function useMarketSelectorContext() {
  const context = useContext(MarketSelectorContext);
  if (context === undefined) {
    throw new Error('Missing MarketSelectorContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

type MarketSelectorContextProviderProps = {
  marketSelectorAPI: MarketSelectorAPI;
  showCredentialsOnlyToggle?: boolean;
  disableAggregations?: boolean;
  isNarrow: boolean;
  children: ReactNode;
};

/**
 * This provider exists to make it easier to consume the state and api returned from the useMarketSelector hook as well as common state
 * which can be useful for several views.
 */
export const MarketSelectorContextProvider = function MarketSelectorContextProvider({
  children,
  marketSelectorAPI,
  isNarrow,
  showCredentialsOnlyToggle = false,
  disableAggregations = false,
}: MarketSelectorContextProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { preset } = marketSelectorAPI;
  // We want to initially show the market selector blotter, in narrow mode if there is no preset selected
  const [showMarketSelectorBlotter, setShowMarketSelectorBlotter] = useState(preset == null);
  const {
    advancedMode: inputAdvancedMode,
    setAdvancedMode,
    showDisabledItems,
    setShowDisabledItems,
    credentialsOnly: inputCredentialsOnly,
    setCredentialsOnly,
    selectionListShowAll,
    setSelectionListShowAll,
  } = useMarketSelectorConfig();

  // We "wrap" the advancedMode boolean here. We only show advanced mode if there is anything advanced to show.
  // Currently, the only thing to check for this is if there are any enrichments.
  const showAdvancedMode = marketSelectorAPI.enrichments.length > 0;
  const advancedMode = inputAdvancedMode && showAdvancedMode;

  // This boolean can only be true if the toggle itself is even shown to the user
  const credentialsOnly = showCredentialsOnlyToggle && inputCredentialsOnly;

  const [blotterSearchInput, setBlotterSearchInput] = useState<HTMLInputElement | null>(null);
  const value = useMemo(
    () => ({
      showAdvancedMode,
      advancedMode,
      setAdvancedMode,
      showDisabledItems,
      setShowDisabledItems,
      credentialsOnly,
      setCredentialsOnly,
      selectionListShowAll,
      setSelectionListShowAll,
      showCredentialsOnlyToggle,
      disableAggregations,
      narrowModeConfig: {
        showMarketSelectorBlotter,
        setShowMarketSelectorBlotter,
      },
      isOpen,
      setIsOpen,
      ...marketSelectorAPI,
      isNarrow,
      blotterSearchInput,
      setBlotterSearchInput,
    }),
    [
      showAdvancedMode,
      advancedMode,
      setAdvancedMode,
      showDisabledItems,
      setShowDisabledItems,
      credentialsOnly,
      showCredentialsOnlyToggle,
      disableAggregations,
      setCredentialsOnly,
      selectionListShowAll,
      setSelectionListShowAll,
      marketSelectorAPI,
      showMarketSelectorBlotter,
      setShowMarketSelectorBlotter,
      isNarrow,
      isOpen,
      setIsOpen,
      blotterSearchInput,
      setBlotterSearchInput,
    ]
  );
  return <MarketSelectorContext.Provider value={value}>{children}</MarketSelectorContext.Provider>;
};
